import { RD_PRODUCT_CODE } from '@/app/config/products.config';
import type { RdQuoteStepProps } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import { QuoteBreakdown } from '@/features/quotes/components/rd-equipamentos/steps/sections/quote-breakdown.component';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useRdLifecycle } from '@/features/quotes/hooks/rd-equipamentos/use-rd-lifecycle.hook';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { useRdProposal } from '@/features/quotes/hooks/rd-equipamentos/use-rd-proposal.hook';
import { useRdTransmission } from '@/features/quotes/hooks/rd-equipamentos/use-rd-transmission.hook';
import { useRdQuote } from '@/features/quotes/hooks/rd-equipamentos/use-rd-quote.hook';
import { useOwnBackendPolicyPdf } from '@/features/policies/hooks/use-own-backend-policy-pdf.hook';
import { useRdDocumentPdf } from '@/features/policies/hooks/use-rd-document-pdf.hook';
import { useManualEmissionAction } from '@/features/integrations/hooks/use-manual-emission-action.hook';
import { ManualEmissionModal } from '@/features/integrations/components/manual-emission-modal.component';
import { GeneratePdfDialog } from '@/features/quotes/components/quote-steps/generate-pdf.dialog';
import { ModalConfirmTransmit } from '@/features/proposals/components/proposal-steps/modal-confirm-transmit.component';
import { toaster } from '@/shared/ui/toaster.component';
import type { RdStatus } from '@/services/quotes/rd-equipamentos/types';
import type { ProductEmissionMode } from '@/services/product-settings';
import {
  Alert,
  Button,
  Spinner,
  Text,
  Timeline,
} from '@noorden-insurtech/alba-design-system';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  FileDown,
  Printer,
  Send,
} from 'lucide-react';
import { useRef, useState } from 'react';
import {
  StepActions,
  StepActionsGroup,
} from './sections/step-actions.component';

/**
 * Ordem das etapas do ciclo de vida da proposta RD Equipamentos, usada para
 * derivar quais nós da timeline já foram alcançados a partir do status
 * atual — `aguardando_analise`/`aprovada` são ramos intermediários entre
 * "Cotada" e "Aceita" (não têm nó próprio na timeline, que tem só 4 nós,
 * espelhando o demo `etapa_emissao.html`).
 */
const STATUS_RANK: Record<RdStatus, number> = {
  rascunho: 0,
  cotada: 1,
  aguardando_analise: 1,
  aprovada: 2,
  aceita: 3,
  emitida: 4,
  cancelada: -1,
};

type TimelineDateParts = { day: number; month: string; time: string };

function toTimelineDate(iso: string | undefined): TimelineDateParts | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return {
    day: date.getDate(),
    month: date.toLocaleString('pt-BR', { month: 'short' }),
    time: date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}

type TimelineNode = {
  key: string;
  title: string;
  done: boolean;
  date: TimelineDateParts | null;
};

/**
 * Nós da timeline de status (Rascunho → Cotada → Proposta gerada →
 * Transmitida → Emissão no i4 Pro), "concluído" derivado do status atual da
 * proposta via `STATUS_RANK` — exceto "Transmitida", que o Motor não tem
 * (vem do BFF, `useRdTransmission`).
 * Datas só são exibidas onde temos um timestamp real (`createdAt` para o
 * rascunho, `emission.issuedAt` para a emissão) — não fabricamos datas para
 * as transições intermediárias, que a proposta não audita individualmente.
 */
function buildTimelineNodes(
  status: RdStatus,
  createdAt: string,
  emission: { protocol: string; issuedAt: string } | undefined,
  emissionMode: ProductEmissionMode | undefined,
  isTransmitted: boolean
): TimelineNode[] {
  const rank = STATUS_RANK[status];
  const isEmitida = status === 'emitida';
  const pendingEmissionTitle = !isTransmitted
    ? 'Emissão no i4 Pro'
    : emissionMode === 'INTEGRATION'
      ? 'Emissão automática em processamento no i4 Pro'
      : 'Aguardando emissão manual no i4 Pro';

  return [
    {
      key: 'rascunho',
      title: 'Rascunho',
      done: rank >= 0,
      date: toTimelineDate(createdAt),
    },
    {
      key: 'cotada',
      title: 'Cotada',
      done: rank >= 1,
      date: null,
    },
    {
      key: 'aceita',
      title: 'Proposta gerada',
      done: rank >= 3,
      date: null,
    },
    {
      key: 'transmitida',
      title: 'Transmitida',
      done: isTransmitted,
      date: null,
    },
    {
      key: 'emissao',
      title:
        isEmitida && emission
          ? `Emitido no i4 Pro — apólice ${emission.protocol}`
          : pendingEmissionTitle,
      done: isEmitida,
      date: isEmitida ? toTimelineDate(emission?.issuedAt) : null,
    },
  ];
}

/**
 * Step 6 — Emissão (RD Equipamentos, ramo 0171): breakdown da cotação
 * (`QuoteBreakdown`, compartilhado com o Step 5 — Cotação) + timeline de
 * status + ações (demo `etapa_emissao.html`):
 * - `Imprimir apólice` — sempre visível, habilitado só em `emitida`.
 *   Baixa o PDF da FOLHETERIA (motor → `policy-pdf`), o mesmo documento
 *   que a lista de Apólices entrega e o mesmo que a rota `/apolice`
 *   exibe no "Ver detalhes". Antes era um link para uma réplica HTML
 *   montada no front: o mesmo segurado recebia documentos de origens
 *   diferentes conforme imprimisse pelo wizard ou pela lista (achado do
 *   QA de 2026-08-21).
 * - `Emitir manualmente` — só em `aceita`, só para quem pode concluir a
 *   emissão manual (`useManualEmissionAction`, mesmo botão/modal/endpoint
 *   genéricos usados pelos demais produtos em `/integrations`): abre o
 *   modal para informar o nº da apólice no i4Pro, valida contra o i4Pro
 *   real e confirma a emissão. Quem não pode só vê o aviso "Aguardando
 *   emissão manual" — visível SÓ quando o produto está em modo MANUAL
 *   (`manualEmission.emissionMode`, resolvido em `/settings/products`); em
 *   modo INTEGRATION o aviso é o de emissão automática em processamento.
 * - `Baixar Proposta` — PDF da proposta sob demanda (`useRdDocumentPdf`),
 *   disponível em qualquer status, útil inclusive enquanto aguarda emissão.
 * - `Transmitir` — só em `aceita` ainda não transmitida: confirma no mesmo
 *   modal do Empresarial (`ModalConfirmTransmit`) e agenda a emissão
 *   (`POST /transmit`), que segue o Modo de emissão do produto. Os avisos de
 *   emissão e o "Emitir manualmente" só valem depois da transmissão.
 * - `Alert` de sucesso com o protocolo quando `emitida`.
 */
export function StepEmissao({
  onBack,
  canGoBack = true,
  isViewMode = false,
}: RdQuoteStepProps) {
  const { id, policyId } = useQuoteParams();
  const resolvedProposalId = (id || policyId || '').trim();

  const { data: proposal, isLoading: isProposalLoading } = useRdProposal(
    resolvedProposalId || undefined
  );
  const { data: quote, isLoading: isQuoteLoading } = useRdQuote(
    resolvedProposalId || undefined,
    !!resolvedProposalId
  );

  const status = proposal?.status;
  const { data: transmission, isLoading: isTransmissionLoading } =
    useRdTransmission(resolvedProposalId || undefined, status === 'aceita');
  const isTransmitted =
    status === 'emitida' || transmission?.transmitted === true;
  // Proposta gerada, transmitida, ainda sem apólice.
  const isAwaitingEmission = status === 'aceita' && isTransmitted;
  const showTransmitir =
    status === 'aceita' && transmission?.transmitted === false;

  const { transmit } = useRdLifecycle(resolvedProposalId || undefined);
  const [isTransmitOpen, setIsTransmitOpen] = useState(false);
  // Mesma trava do Step 5 (`isActionRunningRef` em `step-cotacao`):
  // `isPending` só vira `true` no próximo render, então um clique duplo no
  // "Confirmar Transmissão" dispararia dois `POST /transmit`.
  const isTransmittingRef = useRef(false);

  async function handleConfirmTransmit() {
    if (isTransmittingRef.current) return;
    isTransmittingRef.current = true;
    try {
      await transmit.mutateAsync();
      setIsTransmitOpen(false);
      toaster.create({
        type: 'success',
        title: 'Proposta transmitida',
        description: 'A proposta foi enviada para emissão da apólice.',
      });
    } catch (error) {
      setIsTransmitOpen(false);
      toaster.create({
        type: 'error',
        title: 'Erro ao transmitir proposta',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    } finally {
      isTransmittingRef.current = false;
    }
  }

  const manualEmission = useManualEmissionAction({
    policyId: resolvedProposalId,
    productCode: RD_PRODUCT_CODE,
    isEligible: isAwaitingEmission,
  });
  const isAwaitingManualEmission =
    isAwaitingEmission && manualEmission.emissionMode === 'MANUAL';
  const isAwaitingAutomaticEmission =
    isAwaitingEmission && manualEmission.emissionMode === 'INTEGRATION';

  const blockInteraction =
    isProposalLoading || isQuoteLoading || isTransmissionLoading;
  // `isViewMode` NÃO entra aqui: "Transmitir"/"Emitir manualmente" continuam
  // disponíveis em modo visualização — mesma decisão já aplicada ao Step 5
  // (Cotação).
  const disabled = blockInteraction;

  // Só há apólice após a emissão — antes disso a rota de impressão mostra o
  // guard "ainda não tem apólice", então o link ficaria num beco sem saída.
  const canPrint = status === 'emitida';
  const showSuccessAlert = status === 'emitida' && !!proposal?.emission;

  const policyPdf = useOwnBackendPolicyPdf();
  const documentPdf = useRdDocumentPdf();
  // Corretora inativa: a Api barra o PDF da proposta (`document-pdf`) e o da
  // apólice (`policy-pdf`) com 403.
  const proposalActionGuard = useSelectedBrokerWriteGuard('proposal', 'other');
  const policyActionGuard = useSelectedBrokerWriteGuard('policy', 'other');
  // Ambos os botões ficam visíveis independente do status, por ora (PO
  // 2026-09-17): "Imprimir apólice" segue exigindo `emitida`; "Baixar
  // Proposta" (aqui embaixo) não exige nada — inclusive útil enquanto
  // aguarda a emissão manual, que é exatamente o caso relatado.
  const [isDownloadPropostaOpen, setIsDownloadPropostaOpen] = useState(false);

  return (
    <div className='relative flex flex-col gap-8'>
      {isProposalLoading || isQuoteLoading ? (
        <div
          className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/80'
          aria-busy
          aria-label='Carregando emissão'
        >
          <Spinner size='md' />
          <Text className='text-sm text-alba-black-400'>
            Carregando emissão...
          </Text>
        </div>
      ) : null}

      <div className='flex flex-col gap-1'>
        <Text className='text-xl font-semibold text-alba-black-500'>
          Emissão
        </Text>
        <Text className='text-sm text-alba-gray-500'>
          Revise a cotação, confirme a emissão e imprima a apólice.
        </Text>
      </div>

      {proposal && quote ? (
        <QuoteBreakdown quote={quote} proposal={proposal} />
      ) : null}

      {proposal ? (
        <Timeline.Root>
          <Timeline.Heading className='text-alba-black-500 text-base'>
            Linha do tempo da proposta
          </Timeline.Heading>
          <Timeline.List>
            {buildTimelineNodes(
              proposal.status,
              proposal.createdAt,
              proposal.emission,
              manualEmission.emissionMode,
              isTransmitted
            ).map(node => (
              <Timeline.Item key={node.key}>
                {/* O Timeline.Item é um grid fixo [data | marcador | card]; os
                    filhos se auto-posicionam por ordem no DOM. Nós sem data ainda
                    precisam ocupar a 1ª coluna (a linha conectora fica fixa nela),
                    senão o marcador sai da linha e o card colapsa. */}
                {node.date ? (
                  <Timeline.Date
                    day={node.date.day}
                    month={node.date.month}
                    time={node.date.time}
                  />
                ) : (
                  <div aria-hidden />
                )}
                <Timeline.Marker />
                <Timeline.Card size='full'>
                  <Timeline.CardTitle className='flex items-center gap-2'>
                    {node.done ? (
                      <CheckCircle2
                        className='h-4 w-4 shrink-0 text-alba-success-green-500'
                        strokeWidth={1.8}
                      />
                    ) : (
                      <Circle
                        className='h-4 w-4 shrink-0 text-alba-gray-400'
                        strokeWidth={1.8}
                      />
                    )}
                    {node.title}
                  </Timeline.CardTitle>
                </Timeline.Card>
              </Timeline.Item>
            ))}
          </Timeline.List>
        </Timeline.Root>
      ) : null}

      {showSuccessAlert ? (
        <Alert.Root status='success'>
          <Alert.Icon />
          <Alert.Content>
            <Alert.Title>Apólice emitida</Alert.Title>
            <Alert.Description>
              Apólice no i4 Pro: {proposal?.emission?.protocol}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {isAwaitingManualEmission && !showSuccessAlert ? (
        <Alert.Root status='info'>
          <Alert.Icon />
          <Alert.Content>
            <Alert.Title>Aguardando emissão manual</Alert.Title>
            <Alert.Description>
              Esta proposta foi transmitida e só vira apólice quando um
              administrador informar o número da apólice no i4Pro.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {isAwaitingAutomaticEmission && !showSuccessAlert ? (
        <Alert.Root status='info'>
          <Alert.Icon />
          <Alert.Content>
            <Alert.Title>Emissão em processamento</Alert.Title>
            <Alert.Description>
              Esta proposta foi transmitida. A emissão da apólice no i4Pro está
              sendo processada automaticamente.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <StepActions>
        <Button
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={isViewMode || !canGoBack || blockInteraction}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>

        <StepActionsGroup>
          <Button
            variant='secondary'
            size='md'
            className='flex items-center gap-2'
            loading={policyPdf.isPending}
            disabled={
              !canPrint ||
              !resolvedProposalId ||
              policyPdf.isPending ||
              policyActionGuard.blocked
            }
            title={policyActionGuard.reason}
            onClick={() => {
              if (resolvedProposalId)
                policyPdf.mutate({
                  productCode: RD_PRODUCT_CODE,
                  id: resolvedProposalId,
                });
            }}
          >
            <Printer className='h-5 w-5' strokeWidth={1.6} />
            Imprimir apólice
          </Button>

          <Button
            variant='secondary'
            size='md'
            className='flex items-center gap-2 whitespace-nowrap bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:[&>svg]:text-[#129440]'
            onClick={() => setIsDownloadPropostaOpen(true)}
            disabled={
              !resolvedProposalId ||
              blockInteraction ||
              proposalActionGuard.blocked
            }
            title={proposalActionGuard.reason}
          >
            <FileDown className='h-5 w-5' strokeWidth={2} />
            Baixar Proposta
          </Button>

          {showTransmitir ? (
            <Button
              variant='primary'
              size='md'
              className='flex items-center gap-2'
              loading={transmit.isPending}
              disabled={
                disabled || transmit.isPending || proposalActionGuard.blocked
              }
              title={proposalActionGuard.reason}
              onClick={() => setIsTransmitOpen(true)}
            >
              <Send className='h-5 w-5' strokeWidth={1.6} />
              Transmitir
            </Button>
          ) : null}

          {manualEmission.isVisible ? (
            <Button
              variant='primary'
              size='md'
              className='flex items-center gap-2'
              disabled={disabled}
              onClick={manualEmission.open}
            >
              Emitir manualmente
            </Button>
          ) : null}
        </StepActionsGroup>
      </StepActions>

      <ModalConfirmTransmit
        isOpen={isTransmitOpen}
        isLoading={transmit.isPending}
        onConfirm={handleConfirmTransmit}
        onClose={() => {
          if (!transmit.isPending) setIsTransmitOpen(false);
        }}
      />

      <ManualEmissionModal
        isOpen={manualEmission.isOpen}
        onClose={manualEmission.close}
        onConfirm={manualEmission.confirm}
        isLoading={manualEmission.isPending}
        errorMessage={manualEmission.errorMessage}
        proposalNo={proposal?.number}
        requiresPolicyPdf={manualEmission.requiresPolicyPdf}
      />

      <GeneratePdfDialog
        isOpen={isDownloadPropostaOpen}
        onClose={() => setIsDownloadPropostaOpen(false)}
        isLoading={documentPdf.isPending}
        title='Baixar Proposta'
        description={
          <>
            <Text className='text-sm text-alba-gray-600'>
              O PDF da proposta será gerado para download.
            </Text>
            <Text className='text-sm text-alba-gray-600'>
              Deseja continuar?
            </Text>
          </>
        }
        onConfirm={async () => {
          if (!resolvedProposalId) return;
          try {
            await documentPdf.mutateAsync({
              proposalId: resolvedProposalId,
              tipo: 'proposta',
            });
            setIsDownloadPropostaOpen(false);
          } catch {
            // Toast já disparado pelo `onError` de `useRdDocumentPdf` — o
            // dialog fica aberto para o corretor tentar de novo.
          }
        }}
      />
    </div>
  );
}
